/**
 * Industry design intelligence. Each entry refines the base brief with what an
 * industry's design must understand: positioning, visual trends, colour and
 * marketing psychology, imagery direction, premium cues, and the genomes /
 * library patterns that suit it. Keyed by industry slug. Additive — new
 * industries are new entries; anything unknown falls back to base + other
 * dimensions. Pure data.
 */

export const INDUSTRY_KNOWLEDGE = Object.freeze({
  restaurant: {
    asOf: '2026', confidence: 0.8,
    industryUnderstanding: 'Restaurants sell appetite and atmosphere; the food and the mood are the message.',
    competitorLandscape: 'Crowded local market; differentiation comes from a strong, appetising visual identity and a clear offer.',
    brandPositioning: ['appetising', 'inviting'],
    visualTrends: ['hero-food-imagery', 'warm-tones', 'menu-led-layouts'],
    colorPsychology: ['warm-tones-stimulate-appetite', 'deep-tones-signal-fine-dining'],
    photographyDirection: ['close-up-food', 'rich-natural-light'],
    ctaBehaviour: ['reserve-or-order-primary'],
    premiumCues: ['crafted-detail', 'considered-color'],
    thingsToAvoid: ['low-quality-food-photos', 'cluttered-menus'],
    recommendedGenomes: ['bold', 'organic', 'editorial'],
    recommendedPatterns: ['image-band', 'split-hero', 'centered-poster'],
  },
  cafe: {
    asOf: '2026', confidence: 0.75,
    industryUnderstanding: 'Cafes sell a cosy, artisanal ritual; the identity should feel hand-crafted and local.',
    brandPositioning: ['artisanal', 'cosy'],
    visualTrends: ['warm-neutrals', 'lifestyle-imagery', 'hand-crafted-feel'],
    colorPsychology: ['warm-neutrals-feel-comforting'],
    photographyDirection: ['lifestyle', 'interiors'],
    premiumCues: ['tactile', 'crafted-detail'],
    recommendedGenomes: ['organic', 'scandinavian', 'playful'],
    recommendedPatterns: ['image-band', 'minimal-stack'],
  },
  bakery: {
    asOf: '2026', confidence: 0.72,
    industryUnderstanding: 'Bakeries sell sweetness and warmth; soft, inviting design with close-up texture wins.',
    brandPositioning: ['sweet', 'inviting'],
    visualTrends: ['soft-palettes', 'close-up-textures'],
    colorPsychology: ['pastels-feel-sweet-and-gentle'],
    photographyDirection: ['close-up-textures', 'bright-airy'],
    recommendedGenomes: ['playful', 'organic', 'retro'],
    recommendedPatterns: ['card-stack', 'image-band'],
  },
  medical: {
    asOf: '2026', confidence: 0.82,
    industryUnderstanding: 'Healthcare sells trust and calm competence; clarity and reassurance matter more than flair.',
    competitorLandscape: 'Trust and credentials are the differentiators; visual restraint reads as professionalism.',
    brandPositioning: ['trustworthy', 'calm', 'professional'],
    visualTrends: ['clean-whitespace', 'cool-calming-palettes', 'reassuring-imagery'],
    colorPsychology: ['blues-and-greens-signal-calm-and-health'],
    photographyDirection: ['bright-airy', 'caring-human-moments'],
    ctaBehaviour: ['book-appointment-primary'],
    premiumCues: ['precision', 'generous-whitespace'],
    thingsToAvoid: ['clutter', 'alarming-reds', 'clip-art'],
    recommendedGenomes: ['premium-medical', 'minimal', 'corporate'],
    recommendedPatterns: ['split-hero', 'feature-row', 'stat-row'],
  },
  dental: {
    asOf: '2026', confidence: 0.78,
    industryUnderstanding: 'Dental care sells confident, painless, modern treatment; bright and clinical yet friendly.',
    brandPositioning: ['clean', 'friendly', 'modern'],
    visualTrends: ['bright-whites', 'cool-blues', 'smile-imagery'],
    colorPsychology: ['clean-whites-and-blues-signal-hygiene'],
    photographyDirection: ['bright-airy', 'smiling-patients'],
    recommendedGenomes: ['premium-medical', 'minimal', 'modern'],
    recommendedPatterns: ['split-hero', 'feature-row'],
  },
  'real-estate': {
    asOf: '2026', confidence: 0.8,
    industryUnderstanding: 'Real estate sells aspiration and trust; architectural imagery and precise, confident type carry it.',
    competitorLandscape: 'Agents compete on prestige and reliability; premium restraint signals higher-value listings.',
    brandPositioning: ['aspirational', 'precise', 'trustworthy'],
    visualTrends: ['architectural-photography', 'restrained-navy-and-neutral', 'clear-listing-facts'],
    colorPsychology: ['navy-and-neutral-read-premium-and-stable'],
    photographyDirection: ['architectural', 'wide-cinematic'],
    ctaBehaviour: ['view-listing-or-tour-primary'],
    premiumCues: ['refined-type', 'confident-scale'],
    recommendedGenomes: ['luxury', 'editorial', 'corporate'],
    recommendedPatterns: ['split-hero', 'magazine-spread', 'image-band'],
  },
  fitness: {
    asOf: '2026', confidence: 0.8,
    industryUnderstanding: 'Fitness sells energy and transformation; bold, high-contrast, motion-charged design.',
    brandPositioning: ['energetic', 'motivating'],
    visualTrends: ['high-contrast', 'dynamic-diagonals', 'action-imagery'],
    colorPsychology: ['high-energy-accents-drive-action'],
    photographyDirection: ['action', 'moody'],
    ctaBehaviour: ['join-or-start-primary', 'oversized-cta'],
    premiumCues: ['confident-scale'],
    thingsToAvoid: ['low-contrast', 'timid-cta'],
    recommendedGenomes: ['bold', 'neo-brutalism', 'modern'],
    recommendedPatterns: ['centered-poster', 'feature-row', 'stat-row'],
  },
  salon: {
    asOf: '2026', confidence: 0.76,
    industryUnderstanding: 'Salons sell glamour and self-care; chic, polished, aspirational visuals.',
    brandPositioning: ['chic', 'polished'],
    visualTrends: ['blush-and-gold', 'glamorous-portraits', 'elegant-spacing'],
    colorPsychology: ['blush-and-gold-signal-beauty-and-luxury'],
    photographyDirection: ['editorial', 'portrait'],
    premiumCues: ['refined-type', 'crafted-detail'],
    recommendedGenomes: ['luxury', 'editorial', 'minimal'],
    recommendedPatterns: ['image-band', 'minimal-stack'],
  },
  spa: {
    asOf: '2026', confidence: 0.74,
    industryUnderstanding: 'Spas sell serene, restorative calm; soft, airy, minimal design with breathing room.',
    brandPositioning: ['serene', 'restorative'],
    visualTrends: ['muted-naturals', 'lots-of-whitespace'],
    colorPsychology: ['muted-naturals-feel-calming'],
    photographyDirection: ['soft', 'bright-airy'],
    premiumCues: ['generous-whitespace', 'restraint'],
    recommendedGenomes: ['minimal', 'scandinavian', 'organic'],
    recommendedPatterns: ['minimal-stack', 'image-band'],
  },
  travel: {
    asOf: '2026', confidence: 0.78,
    industryUnderstanding: 'Travel sells escapism and wonder; vivid destination imagery leads, type stays out of the way.',
    brandPositioning: ['aspirational', 'vivid'],
    visualTrends: ['edge-to-edge-photography', 'bright-accents', 'gradient-skies'],
    colorPsychology: ['vivid-color-evokes-escape'],
    photographyDirection: ['destination', 'wide-cinematic'],
    ctaBehaviour: ['explore-deals-primary'],
    recommendedGenomes: ['gradient-mesh', 'editorial', 'modern'],
    recommendedPatterns: ['gradient-hero', 'image-band', 'magazine-spread'],
  },
  hotel: {
    asOf: '2026', confidence: 0.75,
    industryUnderstanding: 'Hotels sell aspirational comfort; serene, luxe imagery and generous space.',
    brandPositioning: ['luxe', 'serene'],
    visualTrends: ['cinematic-imagery', 'muted-luxe-tones'],
    colorPsychology: ['sand-and-slate-read-refined-comfort'],
    photographyDirection: ['wide-cinematic', 'editorial'],
    premiumCues: ['generous-whitespace', 'refined-type'],
    recommendedGenomes: ['luxury', 'editorial', 'minimal'],
    recommendedPatterns: ['magazine-spread', 'image-band'],
  },
  corporate: {
    asOf: '2026', confidence: 0.8,
    industryUnderstanding: 'Corporate sells credibility and clarity; structured, confident, restrained brand systems.',
    competitorLandscape: 'Credibility is table stakes; disciplined systems separate leaders from the rest.',
    brandPositioning: ['credible', 'modern', 'clear'],
    visualTrends: ['structured-grids', 'brand-forward-color', 'confident-sans-type'],
    colorPsychology: ['brand-color-anchors-recognition'],
    photographyDirection: ['studio-clean', 'team-and-workplace'],
    ctaBehaviour: ['learn-more-or-contact-primary'],
    premiumCues: ['precision', 'confident-scale'],
    recommendedGenomes: ['corporate', 'swiss', 'modern'],
    recommendedPatterns: ['feature-row', 'stat-row', 'card-stack'],
  },
  technology: {
    asOf: '2026', confidence: 0.82,
    industryUnderstanding: 'Technology sells the future and precision; clean, modular, UI-inspired composition with subtle depth.',
    brandPositioning: ['modern', 'precise', 'forward'],
    visualTrends: ['bento-layouts', 'gradients', 'glass-and-depth', 'geometric-sans'],
    colorPsychology: ['cool-violets-and-blues-signal-innovation'],
    photographyDirection: ['none', 'abstract-3d'],
    illustrationDirection: ['isometric', '3d', 'abstract-shapes'],
    ctaBehaviour: ['start-free-or-demo-primary'],
    premiumCues: ['subtle-depth', 'precision'],
    recommendedGenomes: ['future-ui', 'bento', 'glassmorphism'],
    recommendedPatterns: ['bento-grid', 'gradient-hero', 'feature-row'],
  },
  education: {
    asOf: '2026', confidence: 0.76,
    industryUnderstanding: 'Education sells credibility and approachability; organised, friendly, optimistic design.',
    brandPositioning: ['credible', 'approachable', 'optimistic'],
    visualTrends: ['friendly-palettes', 'organised-sections', 'learner-imagery'],
    colorPsychology: ['fresh-blues-and-greens-feel-trustworthy-and-hopeful'],
    photographyDirection: ['bright-airy', 'students-and-campus'],
    ctaBehaviour: ['apply-or-enroll-primary'],
    recommendedGenomes: ['modern', 'playful', 'corporate'],
    recommendedPatterns: ['feature-row', 'card-stack', 'stat-row'],
  },
  fashion: {
    asOf: '2026', confidence: 0.8,
    industryUnderstanding: 'Fashion sells identity and desire; editorial, bold, image-led with dramatic type contrast.',
    brandPositioning: ['editorial', 'bold', 'desirable'],
    visualTrends: ['editorial-imagery', 'dramatic-whitespace', 'oversized-type'],
    colorPsychology: ['monochrome-with-one-accent-reads-high-fashion'],
    photographyDirection: ['editorial', 'moody'],
    premiumCues: ['confident-scale', 'refined-type'],
    recommendedGenomes: ['luxury-fashion', 'editorial', 'bold'],
    recommendedPatterns: ['magazine-spread', 'image-band', 'centered-poster'],
  },
  jewelry: {
    asOf: '2026', confidence: 0.77,
    industryUnderstanding: 'Jewelry sells refined luxury; dark or ivory backdrops, macro sparkle, and lots of restraint.',
    brandPositioning: ['refined', 'luxurious'],
    visualTrends: ['macro-product', 'gold-and-ivory', 'negative-space'],
    colorPsychology: ['gold-and-deep-tones-signal-prestige'],
    photographyDirection: ['studio-clean', 'macro-detail'],
    premiumCues: ['restraint', 'generous-whitespace', 'crafted-detail'],
    recommendedGenomes: ['luxury', 'luxury-fashion', 'minimal'],
    recommendedPatterns: ['image-band', 'minimal-stack'],
  },
  wedding: {
    asOf: '2026', confidence: 0.75,
    industryUnderstanding: 'Weddings sell romance and elegance; soft, graceful, editorial layouts with delicate detail.',
    brandPositioning: ['romantic', 'elegant'],
    visualTrends: ['soft-neutrals', 'delicate-motifs', 'airy-symmetry'],
    colorPsychology: ['blush-sage-and-gold-feel-romantic-and-premium'],
    photographyDirection: ['editorial', 'soft'],
    premiumCues: ['crafted-detail', 'refined-type'],
    recommendedGenomes: ['editorial', 'organic', 'luxury'],
    recommendedPatterns: ['magazine-spread', 'image-band'],
  },
  events: {
    asOf: '2026', confidence: 0.76,
    industryUnderstanding: 'Events sell excitement and urgency; bold, energetic, high-impact design with a clear date and ticket action.',
    brandPositioning: ['exciting', 'energetic'],
    visualTrends: ['high-energy-color', 'dynamic-shapes', 'large-type'],
    colorPsychology: ['vibrant-color-creates-buzz'],
    photographyDirection: ['moody', 'crowd-and-stage'],
    ctaBehaviour: ['get-tickets-primary', 'prominent-date'],
    recommendedGenomes: ['bold', 'memphis', 'gradient-mesh'],
    recommendedPatterns: ['centered-poster', 'collage-wall', 'gradient-hero'],
  },
  food: {
    asOf: '2026', confidence: 0.74,
    industryUnderstanding: 'Food brands sell freshness and craving; vibrant, wholesome imagery and friendly clarity.',
    brandPositioning: ['fresh', 'appetising'],
    visualTrends: ['vibrant-naturals', 'wholesome-imagery'],
    colorPsychology: ['fresh-greens-and-warm-tones-feel-wholesome'],
    photographyDirection: ['close-up-food', 'bright-airy'],
    recommendedGenomes: ['organic', 'bold', 'playful'],
    recommendedPatterns: ['image-band', 'card-stack'],
  },
  beauty: {
    asOf: '2026', confidence: 0.76,
    industryUnderstanding: 'Beauty sells transformation and confidence; sleek, premium, product-hero visuals.',
    brandPositioning: ['premium', 'aspirational'],
    visualTrends: ['soft-gradients', 'product-hero', 'sleek-type'],
    colorPsychology: ['soft-gradients-and-blush-feel-premium-beauty'],
    photographyDirection: ['studio-clean', 'product-hero'],
    premiumCues: ['refined-type', 'considered-color'],
    recommendedGenomes: ['luxury', 'playful', 'editorial'],
    recommendedPatterns: ['image-band', 'minimal-stack'],
  },
  legal: {
    asOf: '2026', confidence: 0.75,
    industryUnderstanding: 'Legal services sell authority and discretion; dignified, precise, classic structure.',
    brandPositioning: ['authoritative', 'dignified'],
    visualTrends: ['navy-and-gold', 'classic-serif-headings', 'formal-symmetry'],
    colorPsychology: ['navy-and-burgundy-signal-authority'],
    photographyDirection: ['studio-clean'],
    premiumCues: ['refined-type', 'generous-whitespace'],
    recommendedGenomes: ['corporate', 'swiss', 'editorial'],
    recommendedPatterns: ['feature-row', 'stat-row'],
  },
  finance: {
    asOf: '2026', confidence: 0.78,
    industryUnderstanding: 'Finance sells security and competence; structured, data-forward, trustworthy systems.',
    brandPositioning: ['secure', 'competent'],
    visualTrends: ['structured-grids', 'deep-trust-tones', 'clear-data'],
    colorPsychology: ['deep-navy-and-emerald-signal-security'],
    photographyDirection: ['studio-clean'],
    ctaBehaviour: ['open-account-or-consult-primary'],
    premiumCues: ['precision', 'considered-color'],
    recommendedGenomes: ['corporate', 'future-ui', 'swiss'],
    recommendedPatterns: ['stat-row', 'feature-row', 'bento-grid'],
  },
  automotive: {
    asOf: '2026', confidence: 0.74,
    industryUnderstanding: 'Automotive sells power and sleekness; dramatic, dark, high-contrast hero imagery.',
    brandPositioning: ['sleek', 'powerful'],
    visualTrends: ['dark-dramatic', 'dynamic-angles', 'glossy-hero'],
    colorPsychology: ['dark-with-sharp-accent-signals-performance'],
    photographyDirection: ['moody', 'product-hero'],
    ctaBehaviour: ['book-test-drive-primary'],
    recommendedGenomes: ['bold', 'future-ui', 'modern'],
    recommendedPatterns: ['centered-poster', 'image-band'],
  },
  retail: {
    asOf: '2026', confidence: 0.74,
    industryUnderstanding: 'Retail sells value and urgency; bold offers, clear product imagery, unmistakable CTA.',
    brandPositioning: ['bold', 'value-driven'],
    visualTrends: ['oversized-offer-type', 'product-imagery', 'energetic-badges'],
    colorPsychology: ['vivid-accents-drive-urgency'],
    photographyDirection: ['product-hero', 'bright-airy'],
    ctaBehaviour: ['shop-now-primary', 'prominent-offer'],
    recommendedGenomes: ['bold', 'playful', 'modern'],
    recommendedPatterns: ['centered-poster', 'card-stack', 'feature-row'],
  },
  festival: {
    asOf: '2026', confidence: 0.74,
    industryUnderstanding: 'Festivals sell celebration and energy; maximal, vibrant, expressive design.',
    brandPositioning: ['celebratory', 'vibrant'],
    visualTrends: ['maximal-color', 'playful-shapes', 'expressive-type'],
    colorPsychology: ['saturated-multichrome-feels-festive'],
    photographyDirection: ['none', 'crowd-and-stage'],
    ctaBehaviour: ['grab-passes-primary'],
    recommendedGenomes: ['memphis', 'bold', 'gradient-mesh'],
    recommendedPatterns: ['collage-wall', 'centered-poster', 'gradient-hero'],
  },
});
